import { createEffect, createResource, createRoot } from "solid-js";
import {
  Me,
  type FeatureKey,
  type Permission,
  type RoleKey,
} from "@conatus/contracts";
import { authStore } from "@/auth/store";
import { api } from "@/lib/api";
import { queryClient } from "@/lib/queryClient";
import { recordDebugEvent, isDebugEnabled } from "@/lib/debug";

let useMeInstanceCount = 0;
let sharedMeResource: ReturnType<typeof createMeResource> | undefined;
let refetchSharedMe: (() => Promise<void>) | undefined;

export const meQueryKey = ["me"] as const;
export const mePermissionsQueryKey = ["me", "permissions"] as const;

export async function fetchMe() {
  const startTime = performance.now();
  if (isDebugEnabled()) {
    recordDebugEvent("me", "fetchMe started");
  }
  try {
    const result = Me.parse(await api<unknown>("/me"));
    const duration = (performance.now() - startTime).toFixed(0);
    if (isDebugEnabled()) {
      recordDebugEvent("me", "fetchMe success", {
        duration,
        hasTenant: Boolean(result.tenant),
        permCount: result.permissions.length,
      });
    }
    return result;
  } catch (err) {
    const duration = (performance.now() - startTime).toFixed(0);
    if (isDebugEnabled()) {
      recordDebugEvent("me-error", "fetchMe failed", {
        duration,
        error: err instanceof Error ? err.message : String(err),
      });
    }
    throw err;
  }
}

function createMeResource() {
  return createRoot(() => {
    let lastState = "";
    const [resource, { refetch }] = createResource(
      () => authStore.isAuthenticated() || undefined,
      () => {
        if (isDebugEnabled()) {
          recordDebugEvent("me-resource", "shared /me resource fetcher called", {
            isAuth: authStore.isAuthenticated(),
            cached: Boolean(queryClient.getQueryData(meQueryKey)),
          });
        }
        return queryClient.fetchQuery({
          queryKey: meQueryKey,
          queryFn: fetchMe,
          staleTime: 60_000,
          retry: 1,
        });
      },
    );

    refetchSharedMe = async () => {
      await refetch();
    };

    createEffect(() => {
      const state = resource.state;
      if (isDebugEnabled() && state !== lastState) {
        lastState = state;
        recordDebugEvent("me-resource", "shared /me resource state", {
          state,
          hasLatest: Boolean(resource.latest),
          source: authStore.isAuthenticated() || undefined,
        });
      }
    });

    return resource;
  });
}

function getSharedMeResource() {
  sharedMeResource ??= createMeResource();
  return sharedMeResource;
}

/**
 * Drops cached `/me` and refetches into the shared Solid resource. Call after
 * server-side changes that affect `Me` (e.g. tenant feature entitlements).
 */
export async function refreshMe(): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: meQueryKey });
  await refetchSharedMe?.();
}

export async function fetchMePermissions() {
  const perms = await api<Permission[]>("/me/permissions");
  return Array.isArray(perms) ? perms : [];
}

/**
 * Fetches and validates the `/me` payload. Disabled until the user has a
 * valid access token.
 *
 * Uses `createResource` (native Solid primitive) rather than TanStack Query's
 * `createQuery`, which has a proxy/resource split that breaks Solid's reactivity
 * tracking after the first successful fetch. `queryClient.fetchQuery` is used
 * inside the fetcher to deduplicate concurrent requests and cache the result.
 */
export function useMe() {
  const debugId = isDebugEnabled() ? ++useMeInstanceCount : 0;

  if (isDebugEnabled()) {
    recordDebugEvent("me-instance", `useMe instance ${debugId} created`);
  }

  const resource = getSharedMeResource();

  const hasUsableData = () => Boolean(resource.latest);
  const isPendingState = () =>
    !hasUsableData() && (resource.state === "unresolved" || resource.state === "pending");

  // Treat "refreshing" as usable data. Solid resources keep `latest`
  // populated during background refetches even while `loading` is true.
  return {
    get data() {
      return resource.latest;
    },
    get isPending() {
      return isPendingState();
    },
    get isLoading() {
      return isPendingState();
    },
    get isSuccess() {
      return hasUsableData() || resource.state === "ready" || resource.state === "refreshing";
    },
    get isError() {
      return resource.state === "errored";
    },
    get error() {
      return resource.error;
    },
    get status() {
      if (resource.state === "errored") return "error";
      if (isPendingState()) return "pending";
      return "success";
    },
  };
}

export function hasPermission(
  me: Me | undefined | (() => Me | undefined),
  perm: Permission
): boolean {
  const m = typeof me === "function" ? me() : me;
  if (!m) return false;
  if (m.isPlatformAdmin) return true;
  return m.permissions.includes(perm);
}

export function hasFeature(me: Me | undefined, feature: FeatureKey): boolean {
  return Boolean(me && me.features.includes(feature));
}

export function hasRole(me: Me | undefined, role: RoleKey): boolean {
  if (!me) return false;
  return me.roles.some((r) => r.roleKey === role && r.status === "Active");
}
